(() => {
  const selector = 'video[autoplay]';
  const managedVideos = new Set();
  const states = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const isVisible = video => {
    if (document.hidden || !video.isConnected) return false;
    const rect = video.getBoundingClientRect();
    const style = getComputedStyle(video);
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 &&
      rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth &&
      style.visibility !== 'hidden' && style.display !== 'none';
  };

  const showFallback = video => {
    const state = states.get(video);
    if (!isVisible(video) || !video.paused) return;
    if (!state.button) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'theme-video-play';
      button.textContent = ({ de: 'Video abspielen', it: 'Riproduci video', en: 'Play video' })[document.documentElement.lang.split('-')[0]] || 'Play video';
      button.addEventListener('click', () => {
        state.userStarted = true;
        state.blocked = false;
        // Safari requires play() directly in the user gesture handler.
        playVideo(video, true);
      });
      const host = video.closest('.hero__inner') || video.parentElement;
      host.classList.add('theme-video-host');
      host.appendChild(button);
      state.button = button;
    }
    state.button.hidden = false;
  };

  const canPlay = video =>
    (!reducedMotion.matches || states.get(video)?.userStarted) && isVisible(video);

  const playVideo = (video, userGesture = false) => {
    const state = states.get(video);
    if (!canPlay(video)) {
      video.pause();
      if (state.button) state.button.hidden = true;
      if (isVisible(video)) showFallback(video);
      return;
    }
    if (!video.paused || (state.pending && !userGesture)) return;
    if (state.blocked && !userGesture) { showFallback(video); return; }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    state.pending = true;
    let playPromise;
    try { playPromise = video.play(); } catch (error) { playPromise = Promise.reject(error); }
    Promise.resolve(playPromise).then(() => {
      state.blocked = false;
      delete video.dataset.themeVideoPlayError;
      if (state.button) state.button.hidden = true;
      if (!canPlay(video)) video.pause();
    }).catch(error => {
      state.blocked = error.name === 'NotAllowedError';
      video.dataset.themeVideoPlayError = error.name;
      showFallback(video);
    }).finally(() => { state.pending = false; });
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const video = entry.target;
          video.dataset.themeVideoVisible = entry.isIntersecting ? 'true' : 'false';

          if (entry.isIntersecting) {
            playVideo(video);
          } else {
            video.pause();
            const button = states.get(video)?.button;
            if (button) button.hidden = true;
          }
        });
      }, { threshold: [0, 0.01] })
    : null;

  const registerVideo = video => {
    if (managedVideos.has(video)) return;
    managedVideos.add(video);
    states.set(video, { pending: false, blocked: false, userStarted: false, button: null });

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    ['loadedmetadata', 'loadeddata', 'canplay'].forEach(event => {
      video.addEventListener(event, () => playVideo(video));
    });
    video.addEventListener('playing', () => {
      const state = states.get(video);
      state.blocked = false;
      if (state.button) state.button.hidden = true;
      if (!canPlay(video)) video.pause();
    });

    if (observer) {
      observer.observe(video);
    } else {
      video.dataset.themeVideoVisible = 'true';
      playVideo(video);
    }
  };

  const registerVideos = root => {
    if (root.matches?.(selector)) registerVideo(root);
    root.querySelectorAll?.(selector).forEach(registerVideo);
  };

  registerVideos(document);

  const mutationObserver = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) registerVideos(node);
      });
    });
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  const syncVideos = () => {
    managedVideos.forEach(video => {
      if (!video.isConnected) {
        observer?.unobserve(video);
        states.get(video)?.button?.remove();
        managedVideos.delete(video);
        return;
      }

      playVideo(video);
    });
  };

  document.addEventListener('visibilitychange', syncVideos);
  window.addEventListener('pageshow', syncVideos);
  window.addEventListener('load', syncVideos, { once: true });
  window.addEventListener('resize', syncVideos, { passive: true });
  window.addEventListener('pagehide', () => {
    managedVideos.forEach(video => video.pause());
  });

  reducedMotion.addEventListener?.('change', syncVideos);
})();
